import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

export interface LoginResponse {
  dealerId: number;
  isService: boolean;
}

/**
 * A service that handles user authentication and manages dealer-related data.
 * This service provides methods to log in and log out users, as well as maintaining
 * relevant authentication and dealer state.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isAuth = false;
  dealerId: number | null = null;
  isService = false;

  /**
   * Handles user login by updating authentication and dealer details.
   *
   * @param {LoginResponse} response - The response object containing authentication and dealer information.
   * @return {void}
   */
  login(response: LoginResponse): void {
    this.isAuth = true;
    this.dealerId = response.dealerId;
    this.isService = response.isService;
  }

  /**
   * Logs the user out by resetting the authentication status.
   *
   * @return {void} This method does not return any value.
   */
  logout(): void {
    this.isAuth = false;
  }
}

/**
 * A guard function that checks whether the user is authenticated. It determines access to a specific route
 * by verifying the authentication status from the `AuthService`.
 *
 * If the user is authenticated, access is granted by returning `true`. If not, the user is redirected
 * to the login page by returning a `UrlTree` object pointing to the `service/login` route.
 *
 * @return {true | UrlTree} Returns `true` if the user is authenticated, otherwise a `UrlTree` object representing the login route.
 */
export function serviceAuthGuard(): true | UrlTree {
  const router = inject(Router);
  const authSvc = inject(AuthService);

  return authSvc.isAuth ? true : router.parseUrl('service/login');
}

/**
 * Determines if a user is authenticated and redirects to the dashboard if authenticated.
 *
 * The method uses dependency injection to retrieve required services, such as the `Router`
 * for navigation and the `AuthService` to check authentication status. If the user is
 * authenticated, it returns a `UrlTree` redirecting to the 'service/dashboard' route.
 * Otherwise, it allows further processing by returning `true`.
 *
 * @return {true | UrlTree} Returns `true` if the user is not authenticated, otherwise a `UrlTree` to redirect the user.
 */
export function loginAuthGuard(): true | UrlTree {
  const router = inject(Router);
  const authSvc = inject(AuthService);

  return authSvc.isAuth ? router.parseUrl('service/dashboard') : true;
}
